import { ComponentsVariants } from '@/components.variants';
import { ComplexTabList } from '@/features/ComplexTabList';
import { ContentList } from '@/features/ContentList';
import { CTA } from '@/features/CTA';
import { MarketoForm } from '@/features/MarketoForm';
import { PowerApps } from '@/features/PowerApps';
import Button from '@/features/shared/Button';
import ContentContainer from '@/features/shared/ContentContainer';
import EmbeddedContent from '@/features/shared/EmbeddedContent';
import ErrorBoundary from '@/features/shared/ErrorBoundary';
import FourFiveColumnContentFn from '@/features/shared/FourFiveColumnContent';
import IconTextGrid from '@/features/shared/IconTextGrid';
import Image from '@/features/shared/Image';
import { MuiContainer } from '@/features/shared/material-ui';
import AccordionGroup from '@/features/shared/portal/AccordionGroup';
import EsTabbedContent from '@/features/shared/portal/EsTabbedContent';
import HorizontalButtonSet from '@/features/shared/portal/HorizontalButtonSet';
import MyBusiness from '@/features/shared/portal/MyBusiness';
import PortalCarousel from '@/features/shared/portal/PortalCarousel';
import ToggleMenu from '@/features/shared/portal/ToggleMenu';
import WelcomeMessage from '@/features/shared/portal/WelcomeMessage';
import Quote from '@/features/shared/Quote';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import SolutionsSummary from '@/features/shared/SolutionsSummary';
import SquareTileCTA from '@/features/shared/SquareTileCTA';
import { CaptureClicksTable } from '@/features/shared/Table';
import ThreeColumnContentFn from '@/features/shared/ThreeColumnContent';
import TwoColumnContentFn from '@/features/shared/TwoColumnContent';
import { Video } from '@/features/Video';
import { useJumpLink } from '@/hooks';
import { AppContext } from '@/store';
import { addJumplink } from '@/store/jumplinks/actions';
import { SpacingSizes } from '@/utils/constants';
import { resolveTheme } from '@/utils/resolvers';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { merge } from 'lodash';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { DetailContentProps } from './DetailContentProps';
import FourFiveColumnContent from './FourFiveColumnContent';
import MultiButtonContent from './MultiButtonContent';
import ThreeColumnContent from './ThreeColumnContent';
import TwoColumnContent from './TwoColumnContent';
import { withVisibility } from '@/HoC/withVisibility';
import { mapContainerPropsToComponentProps } from '@/features/shared/DetailContent/DetailContent.map';

const mapComponentList = ({ variant, components: overrideComponents, ...props }: any) => {
  const ComponentList: { [key: string]: any } = {
    link: overrideComponents.Button ?? Button,
    multiLink: overrideComponents.MultiButtonContent ?? MultiButtonContent,
    image: overrideComponents.Image ?? Image,
    [ComponentsVariants.RTC]: overrideComponents.CaptureClicksRichText ?? CaptureClicksRichText,
    video: overrideComponents.Video ?? Video,
    quote: overrideComponents.Quote ?? Quote,
    cta: overrideComponents.CTA ?? CTA,
    twoColumn: TwoColumnContent({
      TwoColumnContentFn,
      ...props,
    }),
    threeColumn: ThreeColumnContent({
      ThreeColumnContentFn,
      ...props,
    }),
    fourFiveColumn: FourFiveColumnContent({
      FourFiveColumnContentFn,
      ...props,
    }),
    squareTileCTA: overrideComponents.SquareTileCTA ?? SquareTileCTA,
    iconTextGrid: overrideComponents.IconTextGrid ?? IconTextGrid,
    solutionsSummary: overrideComponents.SolutionsSummary ?? SolutionsSummary,
    embeddedContent: overrideComponents.EmbeddedContent ?? EmbeddedContent,
    table: overrideComponents.CaptureClicksTable ?? CaptureClicksTable,
    contentList: overrideComponents.ContentList ?? ContentList,
    [ComponentsVariants.TABBED_LIST]: overrideComponents.ComplexTabList ?? ComplexTabList,
    formPicker: overrideComponents.MarketoForm ?? MarketoForm,
    [ComponentsVariants.WELCOME_MESSAGE]: overrideComponents.WelcomeMessage ?? WelcomeMessage,
    [ComponentsVariants.MY_BUSINESS]: overrideComponents.MyBusiness ?? MyBusiness,
    [ComponentsVariants.HORIZONTAL_BUTTON_SET]:
      overrideComponents.HorizontalButtonSet ?? HorizontalButtonSet,
    [ComponentsVariants.PORTAL_CAROUSEL]: overrideComponents.PortalCarousel ?? PortalCarousel,
    [ComponentsVariants.TOGGLE_MENU]: overrideComponents.ToggleMenu ?? ToggleMenu,
    [ComponentsVariants.ACCORDION_GROUP]: overrideComponents.AccordionGroup ?? AccordionGroup,
    [ComponentsVariants.POWER_APPS]: overrideComponents.PowerApps ?? PowerApps,
    [ComponentsVariants.ES_TABBED_CONTENT]: overrideComponents.EsTabbedContent ?? EsTabbedContent,
    [ComponentsVariants.RICH_TEXT]: withVisibility(CaptureClicksRichText),
  };
  return variant && ComponentList[variant];
};

const componentsWithContainer = ['contentList', 'tabbedList'];

const useStyles = makeStyles(
  () =>
    createStyles({
      container: {
        '& > * + *': {
          marginTop: '32px',
        },
      },
      root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        // Form internal padding removed. TwoColumnContent variant displayed an unwanted appearance.
      },
    }),
  { name: 'GenDetailContent' },
);

const validateRequiredFields = ({
  models = {},
  variant = '',
}: {
  models?: any;
  variant?: string;
}) => {
  if (/tabbedList/.test(variant)) {
    // validating tabbedList and tabbedListContent
    return models.content?.tabContent?.length > 0 || models?.tabDetailContentList?.length > 0;
  }

  return true;
};

const withContainer =
  ({
    Component,
    containerProps,
    insetWidth,
    classes,
  }: {
    Component: any;
    containerProps?: any;
    insetWidth?: string;
    classes?: any;
  }) =>
  (props: any) => {
    return (
      <ContentContainer {...containerProps}>
        <MuiContainer className={clsx(classes.container, insetWidth)}>
          <Component {...props} />
        </MuiContainer>
      </ContentContainer>
    );
  };

const DetailContent = ({
  content = [],
  containerProps,
  insetWidth,
  mapVariantToProps,
  mapVariantToClassName,
  theme,
  jumplink,
  anchorLabel = '',
  useContainer,
  className: overrideClassName,
  components: overrideComponents = {},
}: DetailContentProps) => {
  const { DetailContent: OverrideDetailContent, ...otherOverrideComponents } =
    overrideComponents || {};
  const classes = useStyles();
  const { dispatch } = useContext(AppContext);
  const { getJumpLinkIdentifier } = useJumpLink();
  const [componentIdentifier, setComponentIdentifier] = useState<string | undefined>(undefined);

  useEffect(() => {
    const HAS_JUMPLINK = jumplink === true;

    if (HAS_JUMPLINK) {
      const identifier = getJumpLinkIdentifier(anchorLabel);
      setComponentIdentifier(identifier);
      dispatch(
        addJumplink({
          label: anchorLabel,
          href: identifier,
        }),
      );
    }
  }, [jumplink, anchorLabel, dispatch, getJumpLinkIdentifier]);

  const components = useMemo(
    () =>
      content
        .map(({ variant, isAutoLayout, ...props }: any, idx) => {
          const Variant =
            mapComponentList({
              variant,
              DetailContentFn: OverrideDetailContent || DetailContent,
              components: otherOverrideComponents,
              mapVariantToProps,
              mapVariantToClassName,
              isAutoLayout,
            }) || (() => null);
          const isFirst = idx === 0;
          const isLast = idx === content.length - 1;
          const componentTheme =
            theme ||
            resolveTheme('bg-color')({
              rawVal: !isFirst
                ? containerProps?.bottomBackgroundColor
                : containerProps?.topBackgroundColor,
            });
          const className = mapVariantToClassName
            ? mapVariantToClassName({ variant, props, theme: componentTheme })
            : '';
          const variantProps = mapVariantToProps
            ? mapVariantToProps({ variant, props, theme: componentTheme })
            : {};
          const defaultProps = {
            className,
            theme: componentTheme,
          };
          const mergedProps = merge(variantProps, props, defaultProps);
          const shouldUseContainer = useContainer && !componentsWithContainer.includes(variant);

          const ComponentWithContainer = withContainer({
            Component: Variant,
            containerProps: {
              ...containerProps,
              topBackgroundColor: !isFirst
                ? containerProps?.bottomBackgroundColor
                : containerProps?.topBackgroundColor,
              topSpacing: !isFirst ? SpacingSizes.small : containerProps?.topSpacing,
              bottomSpacing: !isLast ? SpacingSizes.small : containerProps?.bottomSpacing,
              insetTop: !isFirst ? undefined : containerProps?.insetTop,
            },
            insetWidth,
            classes,
          });

          const componentProps = mapContainerPropsToComponentProps(
            variant,
            containerProps,
            mergedProps,
          );
          const validFields = validateRequiredFields({
            models: variantProps,
            variant,
          });

          if (!validFields) {
            return null;
          }

          return (
            // eslint-disable-next-line react/no-array-index-key
            <ErrorBoundary key={`${variant}_${idx}`}>
              {shouldUseContainer ? (
                <ComponentWithContainer {...mergedProps} />
              ) : (
                <Variant {...componentProps} />
              )}
            </ErrorBoundary>
          );
        })
        .filter((item) => Boolean(item)),
    [
      content,
      containerProps,
      insetWidth,
      mapVariantToProps,
      mapVariantToClassName,
      theme,
      useContainer,
      classes,
      otherOverrideComponents,
      OverrideDetailContent,
    ],
  );

  const rootClass = clsx(classes.root, overrideClassName, 'detail-content', 'gntc-detail-content');
  return (
    <div className={rootClass}>
      {componentIdentifier && <div id={componentIdentifier} />}
      {components}
    </div>
  );
};

export default memo(DetailContent);
